import React from 'react';
import { AlertCircle, Map } from 'lucide-react';
import { PLACE_TYPE_COLOURS, PLACE_TYPE_LABELS } from '../constants/map';

/* global L */

// Leaflet renders bindPopup(string) as HTML, so every free-text field
// interpolated below (place names, the "as spoken" variant, context notes)
// must be escaped first. These are user- and AI-editable, so an ampersand in
// "Bergen & Belsen" would otherwise garble the popup and a crafted value could
// inject markup.
const escapeHtml = (v) => String(v ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

export default function LeafletMap({ places, isDarkMode, fullscreen = false, focusedPlaceId = null }) {
  const mapRef      = React.useRef(null);
  const mapObjRef   = React.useRef(null);
  const fitTimerRef = React.useRef(null);
  const [loadError, setLoadError] = React.useState(false);
  const [mapReady, setMapReady]   = React.useState(false);
  const fitVisiblePlacesRef = React.useRef(null);
  const fitVisiblePlaces = React.useCallback((map, items) => {
    if (!map) return;
    if (!items || items.length === 0) {
      map.setView([51.5, 19.5], 5);
      return;
    }
    if (items.length === 1) {
      map.setView([items[0].lat, items[0].lon], fullscreen ? 9 : 8);
      return;
    }
    map.fitBounds(L.latLngBounds(items.map(p => [p.lat, p.lon])), {
      padding: fullscreen ? [56, 56] : [48, 48],
      maxZoom: fullscreen ? 8 : 18,
    });
  }, [fullscreen]);
  React.useEffect(() => {
    fitVisiblePlacesRef.current = fitVisiblePlaces;
  }, [fitVisiblePlaces]);
  const refitMap = React.useCallback((map, items) => {
    if (!map) return;
    if (fitTimerRef.current) clearTimeout(fitTimerRef.current);
    const runFit = () => {
      if (!mapObjRef.current || mapObjRef.current !== map) return;
      map.invalidateSize(false);
      fitVisiblePlacesRef.current?.(map, items);
    };
    requestAnimationFrame(runFit);
    fitTimerRef.current = setTimeout(runFit, 180);
  }, []);
  const validPlaces = React.useMemo(
    () => (places || []).filter(p => p.lat && p.lon)
                        .sort((a, b) => (a.year_from || 9999) - (b.year_from || 9999)),
    [places]
  );

  // Inject Leaflet CSS once
  React.useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const link = Object.assign(document.createElement('link'), {
        id: 'leaflet-css', rel: 'stylesheet',
        href: 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
      });
      document.head.appendChild(link);
    }
  }, []);

  React.useEffect(() => {
    const buildMap = () => {
      const L = window.L;
      if (!L || !mapRef.current) return;
      setMapReady(true);

      if (mapObjRef.current) { mapObjRef.current.remove(); mapObjRef.current = null; }

      const tile = isDarkMode
        ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
        : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
      const attr = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                 + (isDarkMode ? ' &copy; <a href="https://carto.com/">CARTO</a>' : '');

      const map = L.map(mapRef.current, { zoomControl: true, scrollWheelZoom: true });
      L.tileLayer(tile, { attribution: attr, maxZoom: 18 }).addTo(map);
      mapObjRef.current = map;

      if (validPlaces.length === 0) { refitMap(map, validPlaces); return; }

      // Journey polyline
      if (validPlaces.length > 1) {
        const lls = validPlaces.map(p => [p.lat, p.lon]);
        L.polyline(lls, { color: '#f59e0b', weight: 2.5, opacity: 0.75, dashArray: '6 4' }).addTo(map);
        // Chevron direction indicators using inline SVG
        for (let i = 0; i < lls.length - 1; i++) {
          const [y1,x1] = lls[i], [y2,x2] = lls[i+1];
          const angle = Math.atan2(y2-y1, x2-x1) * 180 / Math.PI;
          L.marker([(y1+y2)/2, (x1+x2)/2], {
            icon: L.divIcon({
              html: `<div style="transform:rotate(${angle}deg);display:flex;align-items:center;justify-content:center;">
                       <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                         <polyline points="9 18 15 12 9 6"></polyline>
                       </svg>
                     </div>`,
              className: '', iconSize: [14,14], iconAnchor: [7,7]
            })
          }).addTo(map);
        }
      }

      // Inline SVG paths for each place type (avoids emoji rendering inconsistency)
      const PLACE_SVG_PATHS = {
        birthplace:         'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M9 22V12h6v10',
        ghetto:             'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
        labour_camp:        'M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z',
        extermination_camp: 'M12 22C6.5 22 2 17.5 2 12S6.5 2 12 2s10 4.5 10 10-4.5 10-10 10zm0-18v8 M12 16v.01',
        transit:            'M3 8h18l-1 8H4L3 8zm0 0 1-4h16l1 4 M8 8V6m4 2V6m4 2V6',
        liberation:         'M12 2c0 0-5 4-5 9a5 5 0 0 0 10 0c0-5-5-9-5-9z M12 11v1',
        postwar:            'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M9 22V12h6v10',
      };

      // Markers
      validPlaces.forEach((pl, idx) => {
        const colour  = PLACE_TYPE_COLOURS[pl.place_type] || '#6b7280';
        const svgPath = PLACE_SVG_PATHS[pl.place_type] || 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z';
        const label   = pl.canonical || pl.place_name;
        const years   = pl.year_from
          ? `${pl.year_from}${pl.year_to && pl.year_to !== pl.year_from ? `–${pl.year_to}` : ''}`
          : '';

        const icon = L.divIcon({
          html: `<div style="background:${colour};color:#fff;border-radius:50%;width:30px;height:30px;
                   display:flex;align-items:center;justify-content:center;
                   border:2px solid rgba(255,255,255,0.9);box-shadow:0 2px 8px rgba(0,0,0,0.5);position:relative;">
                   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                     <path d="${svgPath}"></path>
                   </svg>
                   <span style="position:absolute;top:-9px;right:-9px;background:#fff;color:#111;
                     border-radius:50%;width:17px;height:17px;font-size:9px;font-weight:800;
                     display:flex;align-items:center;justify-content:center;border:1.5px solid ${colour};">
                     ${idx+1}
                   </span>
                 </div>`,
          className: '', iconSize: [30,30], iconAnchor: [15,15],
        });

        L.marker([pl.lat, pl.lon], { icon }).addTo(map).bindPopup(`
          <div dir="auto" style="font-family:system-ui;min-width:170px;max-width:240px;">
            <div style="font-weight:700;font-size:13px;margin-bottom:3px;">${escapeHtml(label)}</div>
            ${pl.place_name && pl.place_name !== label
              ? `<div style="font-size:10px;color:#888;margin-bottom:5px;">as spoken: "${escapeHtml(pl.place_name)}"</div>` : ''}
            <div style="display:flex;gap:5px;flex-wrap:wrap;margin-bottom:5px;">
              <span style="background:${colour}22;color:${colour};padding:1px 7px;border-radius:10px;font-size:10px;font-weight:700;">
                ${escapeHtml(PLACE_TYPE_LABELS[pl.place_type] || pl.place_type)}
              </span>
              ${years ? `<span style="font-size:10px;color:#666;align-self:center;">${escapeHtml(years)}</span>` : ''}
            </div>
            ${pl.context ? `<div style="font-size:11px;color:#444;font-style:italic;line-height:1.4;">${escapeHtml(pl.context)}</div>` : ''}
          </div>`);
      });

      refitMap(map, validPlaces);
    };

    if (window.L) {
      buildMap();
    } else {
      const s = document.createElement('script');
      s.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      s.onload = buildMap;
      s.onerror = () => setLoadError(true);
      document.head.appendChild(s);
    }
    return () => {
      if (fitTimerRef.current) clearTimeout(fitTimerRef.current);
      if (mapObjRef.current) { mapObjRef.current.remove(); mapObjRef.current = null; }
    };
  }, [validPlaces, isDarkMode, refitMap]);

  React.useEffect(() => {
    const map = mapObjRef.current;
    if (!map || !focusedPlaceId) return;
    const match = validPlaces.find(pl => pl.id === focusedPlaceId);
    if (!match) return;
    map.invalidateSize(false);
    map.setView([match.lat, match.lon], Math.max(map.getZoom() || 0, fullscreen ? 10 : 9), { animate: true });
  }, [focusedPlaceId, validPlaces, fullscreen]);

  React.useEffect(() => {
    if (!mapRef.current) return;
    const node = mapRef.current;
    const map = mapObjRef.current;
    if (!node || !map || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      const liveMap = mapObjRef.current;
      if (!liveMap) return;
      requestAnimationFrame(() => {
        liveMap.invalidateSize(false);
        refitMap(liveMap, validPlaces);
      });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [refitMap, validPlaces]);

  const missing = (places || []).length - validPlaces.length;

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: 0, maxHeight: '100%', overflow: 'hidden' }}>
      <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: 0, maxHeight: '100%', overflow: 'hidden' }}>
        <div ref={mapRef} style={{ width:'100%', height:'100%', borderRadius:'10px',
          overflow:'hidden', border:`1px solid ${isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.10)'}`, zIndex:0 }} />
      {loadError && (
        <div style={{ textAlign:'center', padding:'16px', color:'#ef4444', fontSize:'0.78rem', display:'flex', flexDirection:'column', alignItems:'center', gap:'8px',
          position:'absolute', left:'50%', top:'50%', transform:'translate(-50%,-50%)', zIndex:600, maxWidth:'calc(100% - 24px)',
          background:isDarkMode ? 'rgba(15,23,42,0.88)' : 'rgba(255,255,255,0.92)', borderRadius:'10px', border:`1px solid ${isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.10)'}` }}>
          <AlertCircle size={28} strokeWidth={1.4} style={{opacity:0.6}} />
          Map failed to load. Check your connection and try again.
        </div>
      )}
      {!mapReady && !loadError && (
        <div style={{ position:'absolute', left:'50%', top:'50%', transform:'translate(-50%,-50%)', zIndex:600,
          color:'#6b7280', fontSize:'0.78rem', pointerEvents:'none' }}>
          Loading map…
        </div>
      )}
      {missing > 0 && (
        <div style={{ fontSize:'0.68rem', color:'#f59e0b', padding:'4px 8px',
          background:'#f59e0b12', borderRadius:'6px', display:'flex', alignItems:'center', gap:'6px',
          position:'absolute', left:'12px', bottom:'12px', zIndex:500, maxWidth:'calc(100% - 24px)' }}>
          <AlertCircle size={13} strokeWidth={2} style={{flexShrink:0}} />
          {missing} location{missing > 1 ? 's are' : ' is'} missing coordinates — add lat/lon in the Places tab or re-run Auto-seed.
        </div>
      )}
      {validPlaces.length === 0 && (
        <div style={{ textAlign:'center', padding:'16px', color:'#6b7280', fontSize:'0.78rem', display:'flex', flexDirection:'column', alignItems:'center', gap:'8px',
          position:'absolute', left:'50%', bottom:'20px', transform:'translateX(-50%)', zIndex:500,
          background:isDarkMode ? 'rgba(15,23,42,0.88)' : 'rgba(255,255,255,0.92)', borderRadius:'10px', border:`1px solid ${isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.10)'}` }}>
          <Map size={28} strokeWidth={1.2} style={{opacity:0.4}} />
          No geocoded locations yet. Use Auto-seed to extract and geocode from transcript.
        </div>
      )}
      </div>
    </div>
  );
}
